import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useRef } from 'react';

export interface DashboardMetrics {
  saved_producer_count: number;
  saved_activity_count: number;
  saved_place_count: number;
  has_place: boolean;
  bookings_completed_count: number;
  bookings_cancelled_count: number;
  most_saved_activity: { event_id: string; title: string; count: number } | null;
  range_days: number;
}

export interface TrafficMetrics {
  producer_page_views: number;
  activity_views: number;
  booking_started: number;
  booking_completed: number;
  top_utm_source: string | null;
  period_days: number;
}

export function useProducerDashboardMetrics(producerId: string | undefined, rangeDays: 7 | 30 = 7) {
  const toastShownRef = useRef(false);

  const showErrorToast = () => {
    if (!toastShownRef.current) {
      toastShownRef.current = true;
      toast({
        title: 'Kunde inte hämta insikter',
        description: 'Försök igen om en stund. Om problemet kvarstår, kontakta oss.',
      });
    }
  };

  const dbQuery = useQuery({
    queryKey: ['producer-dashboard-metrics', producerId, rangeDays],
    queryFn: async (): Promise<DashboardMetrics> => {
      const { data, error } = await supabase.rpc('get_producer_dashboard_metrics' as any, {
        p_producer_id: producerId!,
        p_range_days: rangeDays,
      });
      if (error) throw error;
      return data as unknown as DashboardMetrics;
    },
    enabled: !!producerId,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const trafficQuery = useQuery({
    queryKey: ['producer-dashboard-traffic', producerId, rangeDays],
    queryFn: async (): Promise<TrafficMetrics> => {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/analytics-proxy?action=producer-dashboard&producer_id=${producerId}&days=${rangeDays}`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
      });

      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(errBody);
      }

      return res.json();
    },
    enabled: !!producerId,
    staleTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Show a single toast if either query fails
  if (dbQuery.error || trafficQuery.error) {
    showErrorToast();
  }

  return {
    dbMetrics: dbQuery.data ?? null,
    trafficMetrics: trafficQuery.data ?? null,
    isLoading: dbQuery.isLoading || trafficQuery.isLoading,
    error: dbQuery.error || trafficQuery.error,
  };
}
